import { Formik } from 'formik'
import noop from 'lodash/noop'
import React, { FC, useCallback, useEffect, useRef, useState } from 'react'

import { Form } from '@Components/FormElements/Formik'
import { TextField } from '@Components/FormElements/UI/TextField'
import { useTranslation } from '@Globals/Translations'

import styles from './GiftCardCustomAmount.module.scss'

interface AmountData {
  amount: string
}

interface GiftCardCustomAmountProps {
  children?: React.ReactNode
  defaultValue?: string
  hasError: boolean
  max: number
  min: number
  onError: (value: boolean) => void
  setValue: (value: number) => void
}

const GiftCardCustomAmount: FC<GiftCardCustomAmountProps> = ({ children, defaultValue, hasError, max, min, onError, setValue }) => {
  const { t } = useTranslation()
  const [value, setInternalValue] = useState('')

  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  const typings = useCallback((currentTarget) => {
    const newValue = currentTarget.value.replace(/\D/g, '')
    if (+newValue < min) {
      onError(true)
    } else if (+newValue > max) {
      onError(true)
    } else {
      onError(false)
    }
    setInternalValue(newValue)
    setValue(+newValue)
  }, [max, min, onError, setValue])

  return (
    <div className={styles.customAmount}>
      <p>{t('giftcard:GIFT_CARD_CUSTOM_AMOUNT_HEADER', 'Please enter your amount')}</p>
      <Formik<AmountData>
        initialValues={{ amount: '' }}
        onSubmit={noop}
        validateOnBlur={false}
        validateOnChange={false}
      >
        <Form>
          <TextField
            className={styles.textField}
            error={hasError ? t('giftcard:GIFT_CARD_RANGE_ERROR', 'The amount must be between {{arg1}} and {{arg2}}', { arg1: min, arg2: max }) : undefined}
            id="th-missing-amount-on-product"
            inputMode="numeric"
            label={t('giftcard:GIFT_CARD_CUSTOM_AMOUNT_LABEL', 'Enter amount')}
            name="amount"
            onChange={({ currentTarget }) => typings(currentTarget)}
            ref={inputRef}
            type="text"
            value={value || defaultValue}
          />
          {children}
        </Form>
      </Formik>
    </div>
  )
}
export default GiftCardCustomAmount
